import { postStat } from 'api/stats/stats.api'
import React, { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { compressFiltersAndOpenUrlInNewWindow } from 'store/filter/urlFilter.helper'
import { colors } from 'styles/variables'
import Icon from 'ui/atoms/Icon/Icon'
import { useAuth } from 'ui/components/AuthContext/AuthContext'
import TooltipIconButton from 'ui/components/TooltipIconButton/TooltipIconButton'
import { EventPost } from 'views/EventsView/components/events.helper'
import styles from './DeviationSettingsListItem.module.less'

type DeviationSettingsListItemProps = {
  name: string
  prettyName: string
  componentTagName: string
  settings: {
    id: number
    name: string
    value: string
    startDate: string
    endDate: string
    comment: string
    operationalEvent?: EventPost
  }[]
  showButton: boolean
  onEventClick: (eventPost: EventPost | undefined) => void
}

export default function DeviationSettingsListItem({
  name,
  prettyName,
  componentTagName,
  settings,
  showButton,
  onEventClick,
}: DeviationSettingsListItemProps): ReactElement {
  const { t } = useTranslation()
  const { hasAccess } = useAuth()

  const handleClick = (): void => {
    postStat('deviation_settings_menu', `navigate-to-filtered-property-${name}`) //DATA LOG FOR PILOT 1,5

    const url = new URL(location.origin)
    url.pathname = '/digitalTwinDeviationSettings'
    url.searchParams.set('open', `true`)
    // TODO: This is a temporary solution to handle the case for Exergi where they only
    // want to filter on the 'component/' tags. This should be generalized if we want to keep this feature.
    // Use componentTagName as path so the filtering is done correct. Use prettyName as fallback if component/ doesn't exists
    compressFiltersAndOpenUrlInNewWindow(url, `component=${componentTagName || prettyName}`)
  }

  const hasAccessToUMMCheckbox = hasAccess({ submodule: 'operational_event_umm', module: 'events' })

  return (
    <div
      className={styles.DeviationSettingsListItem}
      data-testid="deviation-setting-group" // e2e: one group per target property — do not remove
      data-setting-name={name} // e2e: the property data-id (carries the asset), for asset-based lookup — do not remove
    >
      <div className={styles.DeviationSettingsListItem_Title}>
        <span>{prettyName}</span>
        {showButton && (
          <TooltipIconButton
            icon={'fas fa-external-link'}
            iconColor={colors.info3}
            tooltip={t('Open in new tab')}
            iconSize="small"
            onClick={handleClick}
          />
        )}
      </div>
      {settings.map((setting) => {
        let eventName
        if (setting.operationalEvent) {
          eventName = setting.operationalEvent.name
        }

        const handleEventClick = (): void => {
          onEventClick(setting.operationalEvent)
        }

        return (
          <div
            key={setting.id}
            className={styles.DeviationSettingsListItem_Body}
            data-testid="deviation-setting-row" // e2e: one row per active setting in the panel — do not remove
            data-setting-id={setting.id} // e2e: stable id selector, mirrors data-event-id on calendar bars — do not remove
            data-setting-reason={setting.comment || undefined} // e2e: mirrors data-event-reason on calendar bars — do not remove
          >
            <div className={styles.DeviationSettingsListItem_SettingValue}>
              <span className={styles.DeviationSettingsListItem__large}>{setting.name}</span>: {setting.value}
            </div>
            <div>
              <span className={styles.DeviationSettingsListItem__large}>{t('Period')}</span>:{' '}
              {`${setting.startDate} - ${setting.endDate}`}
            </div>
            <div>
              <span className={styles.DeviationSettingsListItem__large}>{t('Cause')}</span>: {setting.comment}
            </div>
            {setting.operationalEvent && (
              <>
                <div>
                  <span className={styles.DeviationSettingsListItem__large}>{t('Event')}</span>:
                  <span className={styles.DeviationSettingsListItem_OperationalEvent} onClick={handleEventClick}>
                    {' '}
                    {eventName}
                  </span>
                  <Icon
                    icon="fal fa-calendar-week"
                    size="small"
                    data-testid="event-link-icon" // e2e: event-linked marker icon — do not remove
                    tooltip={{ title: t('View event'), arrow: true, placement: 'top' }}
                    onClick={handleEventClick}
                  />
                </div>
                {setting.operationalEvent.require_umm && hasAccessToUMMCheckbox && (
                  <div>
                    <span className={styles.DeviationSettingsListItem__large}>{t('Requires UMM')}</span>: {t('Yes')}
                  </div>
                )}
              </>
            )}
          </div>
        )
      })}
      {settings.length === 0 && (
        <span className={styles.DeviationSettingsListItem__small}>{t('No active settings found')}</span>
      )}
    </div>
  )
}
